import React, { ReactNode } from "react";
import { map, sumBy } from "lodash";
import styled from "styled-components";
import { shorten } from "@backpacker/number";
import TooltipContent from "@charty/tooltip/serie";
import { FontWeight, content, ellipsis, form, transparentize } from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { sanitizeHTML } from "@tootools/dom";
import { parse } from "@tootools/emoji";
import { percent } from "@tootools/number";
import Tooltip from "@ui-kit/tooltip";
import { Gauge } from "../model";
import GaugeIndicator from "./indicator";

export interface IProps {
  gauge: Gauge;
  index: number;
  locale: Locale;
  mainUnit?: "percentage" | "total";
  className?: string;
  width: number;
  tooltip?: (gauge: Gauge) => ReactNode;
  total: number;
  onClickGauge?: (uid: Gauge["id"], range?: any, filter?: Gauge["id"]) => void;
  isScrolling: boolean;
  hovered: string;
  maxCount: number;
  metric?: Metric;
  chartValuesFormat?: "absolute" | "relative";
}

const Root = styled.div<{ isNarrow: boolean }>`
  font-size: 14px;
  color: ${content};
  height: 54px;
  display: flex;
  align-items: center;
  font-weight: ${FontWeight.Normal};
  &:hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
`;

const RootPhantom = styled.div`
  height: 54px;
`;

const Index = styled.div<{ isNarrow: boolean }>`
  flex: 0 0 36px;
  padding-left: 12px;
  align-self: ${(props) => props.isNarrow && "flex-start"};
  padding-top: ${(props) => props.isNarrow && "8px"};
  color: ${(props) => content(props, "secondary")};
`;

const Title = styled.div<{ isNarrow?: boolean }>`
  font-weight: ${FontWeight.Normal};
  ${ellipsis("180px")};
  vertical-align: ${(props) => !props.isNarrow && "middle"};
`;

const GaugeContainer = styled.div`
  flex: 1 0;
`;

const Track = styled.div<{
  percentage: number;
  chartValuesFormat: "absolute" | "relative";
}>`
  height: 10px;
  display: flex;
  border-radius: 10px;
  background-color: ${(props) => content(props, "tertiary")};
  width: ${(props) =>
    props.chartValuesFormat === "absolute" ? `${props.percentage}%` : "100%"};
  /* overflow: hidden; */
`;

const Label = styled.div`
  flex: 0 0 180px;
  > * + * {
    margin-left: 6px;
  }
`;

const HorizontalStackedBarChartGauge = ({
  gauge,
  tooltip,
  index,
  locale,
  className,
  width,
  total,
  onClickGauge,
  isScrolling,
  hovered,
  metric,
  chartValuesFormat,
  maxCount,
}: IProps) => {
  const isNarrow = width < 450;
  const localTotal = sumBy(gauge.series, "count");
  return gauge.isPhantom ? (
    <RootPhantom />
  ) : (
    <Tooltip
      tooltip={
        <TooltipContent
          label={null}
          count={sumBy(gauge.series, "count")}
          locale={locale}
          total={total}
          content={
            <>
              {gauge.label} - {shorten(locale, sumBy(gauge.series, "count"))}{" "}
              {getMetricLabel(locale, metric).toLowerCase()}
            </>
          }
        />
      }
    >
      <Root isNarrow={isNarrow} className={className}>
        <Index isNarrow={isNarrow}>{index}</Index>
        {!isNarrow && (
          <Label>
            <Title dangerouslySetInnerHTML={sanitizeHTML(parse(gauge.label))} />
          </Label>
        )}
        <GaugeContainer>
          {isNarrow && <Title isNarrow={isNarrow}>{gauge.label}</Title>}
          <Track
            chartValuesFormat={chartValuesFormat}
            percentage={percent(gauge.sortCount, maxCount)}
          >
            {map(gauge.series, (g, i) => {
              return (
                <GaugeIndicator
                  isFirst={i === 0}
                  isLast={i === gauge.series.length - 1}
                  key={i}
                  total={total}
                  localTotal={localTotal}
                  isTooltipDisabled={isScrolling}
                  gauge={gauge}
                  onClickGauge={
                    onClickGauge
                      ? () => onClickGauge(gauge.id, null, g.id)
                      : null
                  }
                  serie={g}
                  isNarrow={isNarrow}
                  isHovered={!hovered || hovered === g.id}
                  tooltip={tooltip}
                  locale={locale}
                  chartValuesFormat={chartValuesFormat}
                />
              );
            })}
          </Track>
        </GaugeContainer>
      </Root>
    </Tooltip>
  );
};

export default HorizontalStackedBarChartGauge;
